/* eslint-disable max-len */
/* eslint-disable camelcase */
import { ButtonProps, GridSize } from '@material-ui/core';
import { TimeSpan } from '@moderepo/mode-app-components';
import { Entity, EntityMetricsDefinition, NumericMetricInfo, MetricType, SmartModuleType, TagMetricInfo, EntityModuleType, TimeSeriesAggregation }
    from '@moderepo/mode-internal-apis';
import React, { CSSProperties } from 'react';
import { ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS, EntityUIComponentCollection, isKPIMetric } from '.';
import { BasicXYChart, BASIC_XY_CHART_TYPES } from '../../utils/constants';
import { SettingsErrorLogger } from '../../utils/logger';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue, ReadonlyNonEmptyArray }
    from '../../utils/utils';
import { EntityDetailsTimeSpanControlSettings, FixedTimeRangeControlSettings, FixedTimeSpanControlSettings, isEntityDetailsTimeSpanControlSettings,
    isFixedTimeRangeControlSettings, isFixedTimeSpanControlSettings, isTimeRangeControlSettings, TimeControlType, TimeRangeControlSettings }
    from '../misc';
import { MAX_RAW_DATA_COUNT } from '../time-series/tsUtils';



export interface EntityDetailsUIComponentSettings {
    readonly componentType: string;
    readonly customSettings?: {} | undefined;

    // A flag to temporary hide the component so that we don't need to delete the component settings in the uiSettings. We can just mark it
    // as hidden and remove the flag later.
    readonly hidden?: boolean | undefined;
}


/**
 * The interface to define where the entity components are placed. The areaName must be one of the area defined in
 * EntityDetailsPageLayout.templateAreas and the values must be one of the known components registered in EntityUIComponentCollection.
 * The values can also be an array of EntityDetailsUIComponentSettings if we need customSettings. E.g.
 *
 *      componentsPlacement = {
 *          // This is an example where we just want to display Entity_Children_KPI_Comp in top_area, no special custom settings
 *          top_area: ['Entity_Children_KPI_Comp'],
 *
 *          // This is an example where we want to display Entity_Ranking_Table_Comp in bottom_area AND we also want to customize the component
 *          bottom_area: [{
 *              componentType: 'Entity_Ranking_Table_Comp',
 *              customSettings: {
 *                  // ranking table UI Component custom settings
 *              }
 *          }]
 *      }
 */
export interface EntityDetailsCompsPlacement {
    readonly [areaName: string]: readonly (string | EntityDetailsUIComponentSettings)[] | undefined;
}


/**
 * Type guard function to check if an obj is an EntityDetailsCompsPlacement
 */
export const isEntityDetailsCompsPlacement = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityDetailsCompsPlacement => {
    const settings = obj as EntityDetailsCompsPlacement;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings) || Object.keys(settings).length <= 0) {
        return errorLogger?.logError({
            message: 'Value must be non-empty an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // Make sure every values in the object are valid component types
    if (!Object.entries(settings).every(([areaName, componentTypes]) => {
        if (!(componentTypes instanceof Array)) {
            return errorLogger?.logError({
                message: 'Value must be an Array of components',
                value  : componentTypes,
                path   : [...configPath, areaName],
            }) ?? false;
        }

        // Every items in the componentTypes array must be valid
        if (!componentTypes.every((component, index) => {
            const componentType = typeof component === 'object' ? component.componentType : component;
            const componentInfo = EntityUIComponentCollection.getInstance().getComponent(componentType);

            if (typeof componentType !== 'string' && !isObjectLiteral(componentType)) {
                return errorLogger?.logError({
                    message: 'Value must be a non-empty string or an Object',
                    value  : componentType,
                    path   : typeof component === 'object'
                        ? [...configPath, areaName, `[${index}]`, 'componentType'] : [...configPath, areaName, `[${index}]`],
                }) ?? false;
            }

            if (!componentInfo) {
                return errorLogger?.logError({
                    message: 'Value must be the name of an existing EntityUIComponent',
                    value  : componentType,
                    path   : typeof component === 'object'
                        ? [...configPath, areaName, `[${index}]`, 'componentType'] : [...configPath, areaName, `[${index}]`],
                }) ?? false;
            }

            const customSettingsObject = typeof component === 'object' ? component.customSettings : undefined;
            const customSettingsValidator = componentInfo.customSettingsValidator;

            // If there is a customSettings object AND if there is a validator function to validate the customSettings object,
            // call the validator function to validate the object
            if (customSettingsObject && customSettingsValidator) {
                // NOTE: We will only validate the component's customSettings and log error if we find errors BUT we will not
                // return false. If we return false, this causes the entire entityClass' uiSettings to fail and the UI will use
                // the default view. This error will only affect one specific component so we allow everything to continue.
                customSettingsValidator(customSettingsObject, errorLogger, [...configPath, areaName, `[${index}]`, 'customSettings']);
            }

            return true;
        })) {
            // Not every items in the componentTypes array are valid therefore return false
            return false;
        }

        return true;
    })) {
        // Not every values in the settings object are valid therefore return false
        return false;
    }

    return true;
};


/**
 * This structure is used for defining the layout for each entity details page. It is based of CSS's Grid layout.
 */
export interface EntityDetailsPageLayout {
    // To define the position of the areas for the layout. See CSS's 'templateAreas' documentation for more info. NOTE that unlike CSS's
    // templateAreas which is a string with multiple lines, this is an DOUBLE Array of EntityDetailsArea which will be converted
    // to an array of string. We use double array of EntityDetailsArea to make it easier to validate the value.
    readonly templateAreas: readonly string[][];

    // To define the size of each row specified in the 'templateAreas'. See CSS's 'templateRows' documentation for more info.
    readonly templateRows?: string | undefined;

    // To define the size of each column specified in the 'templateAreas'. See CSS's 'templateColumns' documentation for more info.
    readonly templateColumns?: string | undefined;


    // Optional layout for SMALL screens
    readonly smallScreen?: {
        readonly templateAreas?: readonly string[][] | undefined;
        readonly templateRows?: string | undefined;
        readonly templateColumns?: string | undefined;
    } | undefined,


    // Optional layout for MEDIUM screens
    readonly mediumScreen?: {
        readonly templateAreas?: readonly string[][] | undefined;
        readonly templateRows?: string | undefined;
        readonly templateColumns?: string | undefined;
    } | undefined,
}


/**
 * Type guard function to check if the given object is a valid 'templateAreas' settings
 */
const isLayoutTemplateAreasSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityDetailsPageLayout['templateAreas'] => {
    const settings = obj as EntityDetailsPageLayout['templateAreas'];
    const configPath = [...(currentConfigPath ?? [])];

    if (!(settings instanceof Array) || settings.length <= 0) {
        return errorLogger?.logError({
            message: 'Value must be non-empty double array of strings',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // Make sure ALL values in the array are valid area names
    if (!settings.every((areaArray, index) => {
        // Make sure areaArray is an Array and not empty
        if (!(areaArray instanceof Array) || areaArray.length <= 0) {
            return errorLogger?.logError({
                message: 'Value must be non-empty array of strings',
                value  : areaArray,
                path   : [...configPath, `[${index}]`],
            }) ?? false;
        }

        // Make sure every area in the areaArray are non-empty strings
        if (!areaArray.every((area, areaIndex) => {
            if (typeof area !== 'string' || area.length <= 0) {
                return errorLogger?.logError({
                    message: 'Value must be non-empty string',
                    value  : area,
                    path   : [...configPath, `[${index}]`, `[${areaIndex}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }

        return true;
    })) {
        return false;
    }

    return true;
};


/**
 * Type guard function to check if the given object is a valid 'smallScreen' or 'mediumScreen' settings
 */
const isLayoutScreenSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityDetailsPageLayout['smallScreen'] => {
    const settings = obj as EntityDetailsPageLayout['smallScreen'];
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (
        settings.templateAreas !== undefined
        && !isLayoutTemplateAreasSettings(settings.templateAreas, errorLogger, [...configPath, 'templateAreas'])
    ) {
        return false;
    }

    if (!isValidSettingStringValue(settings.templateRows, false, false)) {
        return errorLogger?.logError({
            message: '"templateRows" must be undefined or a non-empty string',
            value  : settings.templateRows,
            path   : [...configPath, 'templateRows'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.templateColumns, false, false)) {
        return errorLogger?.logError({
            message: '"templateColumns" must be undefined or a non-empty string',
            value  : settings.templateColumns,
            path   : [...configPath, 'templateColumns'],
        }) ?? false;
    }

    return true;
};


/**
 * Type guard function to check if an object is a type of EntityDetailsPageLayout
 */
export const isEntityDetailsPageLayout = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityDetailsPageLayout => {
    const settings = obj as EntityDetailsPageLayout;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isLayoutTemplateAreasSettings(settings.templateAreas, errorLogger, [...configPath, 'templateAreas'])) {
        return false;
    }

    if (!isValidSettingStringValue(settings.templateRows, false, false)) {
        return errorLogger?.logError({
            message: '"templateRows" must be undefined or a non-empty string',
            value  : settings.templateRows,
            path   : [...configPath, 'templateRows'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.templateColumns, false, false)) {
        return errorLogger?.logError({
            message: '"templateColumns" must be undefined or a non-empty string',
            value  : settings.templateColumns,
            path   : [...configPath, 'templateColumns'],
        }) ?? false;
    }

    if (settings.smallScreen !== undefined && !isLayoutScreenSettings(settings.smallScreen, errorLogger, [...configPath, 'smallScreen'])) {
        return false;
    }

    if (settings.mediumScreen !== undefined && !isLayoutScreenSettings(settings.mediumScreen, errorLogger, [...configPath, 'mediumScreen'])) {
        return false;
    }

    return true;
};


/**
 * The structure of the param to be passed to the EntityUIComponent to tell the UI component how to fetch data. This structure tells
 * the UI component to fetch data in the specified 'timeSpan' e.g. 1 day of data, 1 week of data, 1 month of data, etc....
 * and fetch multiple set of those data for each timestamp in the specified array of 'timestamps'
 */
export interface FetchEntityTimeSpanDataParams {
    readonly timeSpan: TimeSpan;

    // An array of timestamps with at least 1 timestamp
    readonly timestamps: ReadonlyNonEmptyArray<number>;
}

/**
 * Type guard function for the FetchEntityTimeSpanDataParams
 */
export const isFetchEntityTimeSpanDataParams = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is FetchEntityTimeSpanDataParams => {
    const settings = obj as FetchEntityTimeSpanDataParams;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.timeSpan, true, false, Object.values(TimeSpan))) {
        return errorLogger?.logError({
            message: `"timeSpan" value is required and must be one of the following [${Object.values(TimeSpan).map((value) => {
                return `"${value}`;
            }).join(', ')}]`,
            value: settings.timeSpan,
            path : [...configPath, 'timeSpan'],
        }) ?? false;
    }

    if (!(settings.timestamps instanceof Array) || settings.timestamps.length <= 0) {
        return errorLogger?.logError({
            message: '"timestamps" must be a non-empty Array of timestamp',
            value  : settings.timestamps,
            path   : [...configPath, 'timestamps'],
        }) ?? false;
    }

    // Make sure all values in timestamps array are numbers
    if (!settings.timestamps.every((value, index) => {
        if (!Number.isInteger(value) || value < 0) {
            return errorLogger?.logError({
                message: 'Every values in "timestamps" Array must be integers greater than 0',
                value,
                path   : [...configPath, 'timestamps', `[${index}]`],
            }) ?? false;
        }
        return true;
    })) {
        return false;
    }

    return true;
};


/**
 * The structure of the param to be passed to the EntityUIComponent to tell the UI component how to fetch data. This structure tells
 * the UI component to fetch data by start/end time. And if it is TSDB data, fetch the data for the specified 'aggregation' function.
 */
export interface FetchEntityTimeRangeDataParams {
    readonly startTime: number;
    readonly endTime: number;
    // The collection aggregation type. Only needed for TSDB data and the default is 'avg'
    readonly aggregation?: TimeSeriesAggregation | undefined;
}

/**
 * Type guard function for the FetchEntityTimeRangeDataParams
 */
export const isFetchEntityTimeRangeDataParams = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is FetchEntityTimeRangeDataParams => {
    const settings = obj as FetchEntityTimeRangeDataParams;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!Number.isInteger(settings.startTime) || settings.startTime < 0) {
        return errorLogger?.logError({
            message: '"startTime" must be an integer greater than 0',
            value  : settings.startTime,
            path   : [...configPath, 'startTime'],
        }) ?? false;
    }

    if (!Number.isInteger(settings.endTime) || settings.endTime < 0) {
        return errorLogger?.logError({
            message: '"endTime" must be an integer greater than 0',
            value  : settings.endTime,
            path   : [...configPath, 'endTime'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.aggregation, false, false, Object.values(TimeSeriesAggregation))) {
        return errorLogger?.logError({
            message: `"aggregation" must be undefined or one of the following values [${Object.values(TimeSeriesAggregation).map((value) => {
                return `"${value}"`;
            }).join(', ')}]`,
            value: settings.endTime,
            path : [...configPath, 'aggregation'],
        }) ?? false;
    }

    return true;
};


/**
 * The structure of the param to be passed to the EntityUIComponent to tell the UI component how to fetch data. This structure tells
 * the UI component to fetch data at the specified timestamp and fetch 'limit' number of data point.
 */
export interface FetchEntityDataCountParams {
    readonly timestamp: number;
    readonly limit: number;
}

/**
 * Type guard function for the FetchEntityDataCountParams
 */
export const isFetchEntityDataCountParams = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is FetchEntityDataCountParams => {
    const settings = obj as FetchEntityDataCountParams;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!Number.isInteger(settings.timestamp) || settings.timestamp < 0) {
        return errorLogger?.logError({
            message: '"timestamp" must be an integer greater than 0',
            value  : settings.timestamp,
            path   : [...configPath, 'timestamp'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.limit, true, false, -MAX_RAW_DATA_COUNT, MAX_RAW_DATA_COUNT)) {
        return errorLogger?.logError({
            message: `"limit" is required and must be an integer between ${-MAX_RAW_DATA_COUNT} and ${MAX_RAW_DATA_COUNT}`,
            value  : settings.limit,
            path   : [...configPath, 'limit'],
        }) ?? false;
    }

    return true;
};

/**
 * Base interface for all EntityUIComponents e.g. component that display metrics, component that display ranking table, components that
 * display entity children, etc... Each component will have its own props interface which extends this interface.
 */
export interface EntityUIComponentProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly entityId: string;

    // The params for fetching data. These params tells the EntityUIComponent how to fetch data e.g. whether to fetch data for a
    // specific timeStamp at some timestamps or fetch data for a specific start/end time range.
    readonly fetchDataParams?: FetchEntityTimeSpanDataParams | FetchEntityTimeRangeDataParams | undefined;

    // Custom settings for each type of UI Component
    readonly customSettings?: any | undefined;

    // On click handler for when the entity component is clicked. If this handler is provided, the component should register onClick event
    // with one of the internal elements and dispatch the event to the handler. It is up the the component to decide when to trigger the
    // onClick event. It could be when the user click on the entire component element. It could be when the user click on a button inside
    // the component.
    readonly onClick?: ((event?: React.MouseEvent<HTMLElement, MouseEvent> | undefined)=> void) | undefined;

    // Handler to show entity details for the specified entity
    readonly onShowEntityDetails?: ((
        entityTarget: Entity | string,
        viewIndex?: number,
        startTime?: number,
        endTime?: number,
    )=> void) | undefined;

    // Handler to show the preview of the specified entity
    readonly onShowEntityPreview?: ((
        entityTarget: Entity | string
    )=> void) | undefined;
}


/**
 * The Base interface for Entity UI Components that display entity children and allow the user to select children. Entity UI components
 * that have this behavior can implement this interface instead of the EntityUIComponentProps
 */
export interface EntityUIComponentWithChildrenProps extends EntityUIComponentProps {
}



/** ************************    BEGIN STRUCTURES FOR CUSTOMIZING THE UI PER ENTITY CLASS   ***************************** */

/**
 * Entity class can use an existing layout AND also add some customization. So in the entityClass.uiSettings.layoutSettings.pageLayoutCustomSettings,
 * we can do this to change the columns size and make 1st column twice as big as second column
 *      entityClass.uiSettings.layoutSettings.pageLayoutCustomSettings = {
 *          templateColumns: '2fr 1fr',
 *      }
 */
export interface CustomizedEntityDetailsPageLayout extends Omit<EntityDetailsPageLayout, 'templateAreas'> {
}


/**
 * Type guard function to check if an object is a type of CustomizedEntityDetailsPageLayout. This function is similar to the
 * type guard function for EntityDetailsPageLayout except that it requires 'layoutName' but it DOES NOT require 'templateAreas'
 */
export const isCustomizedEntityDetailsPageLayout = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is CustomizedEntityDetailsPageLayout => {
    const settings = obj as CustomizedEntityDetailsPageLayout;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.templateRows, false, false)) {
        return errorLogger?.logError({
            message: '"templateRows" must be undefined or a non-empty string.',
            value  : settings.templateRows,
            path   : [...configPath, 'templateRows'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.templateColumns, false, false)) {
        return errorLogger?.logError({
            message: '"templateColumns" must be undefined or a non-empty string.',
            value  : settings.templateColumns,
            path   : [...configPath, 'templateColumns'],
        }) ?? false;
    }

    if (settings.smallScreen !== undefined && !isLayoutScreenSettings(settings.smallScreen, errorLogger, [...configPath, 'smallScreen'])) {
        return false;
    }

    if (settings.mediumScreen !== undefined && !isLayoutScreenSettings(settings.mediumScreen, errorLogger, [...configPath, 'mediumScreen'])) {
        return false;
    }

    return true;
};



/**
 * The structure for defining custom Layout/components placement for each entity class. If an entity class needs to have custom layout or
 * components placement, it can add "uiSettings.layoutSettings" into its' displaySettings props. E.g.
 *
 *      entityClass: {
 *          displaySettings: {
 *              uiSettings: {
 *                  layoutSettings: {
 *                      pageLayout: "double",
 *                      componentsPlacement: {
 *                          left_column: [children],
 *                          right_column: [metrics]
 *                      }
 *                  }
 *              }
 *          }
 *      }
 */
export interface EntityClassLayoutSettings {
    readonly pageLayout?: string         // The name of an existing predefined layout to use
    | EntityDetailsPageLayout           // To use a totally custom page payout and components placement
    | undefined;                        // Use the default layout

    // To override some settings from the selected pageLayout e.g. templateRows, templateColumns
    readonly pageLayoutCustomization?: CustomizedEntityDetailsPageLayout | undefined;

    // To specify where to place the components
    readonly componentsPlacement?: EntityDetailsCompsPlacement | undefined;
}


/**
 * Type guard for EntityClassLayoutSettings. EntityClassLayoutSettings is configured by agent/developer manually so we need to make sure
 * they provided valid settings.
 */
export const isEntityClassLayoutSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityClassLayoutSettings => {
    const settings = obj as EntityClassLayoutSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // pageLayout is optional but if it is provided, it must be valid
    if (settings.pageLayout !== undefined) {
        if (typeof settings.pageLayout !== 'string' && !isObjectLiteral(settings.pageLayout)) {
            return errorLogger?.logError({
                message: '"pageLayout" must be undefined, a non-empty string, or an object.',
                value  : settings.pageLayout,
                path   : [...configPath, 'pageLayout'],
            }) ?? false;
        }

        // If "pageLayout" is a string, make sure it is the name of one of the predefined layout e.g. "default", "double", "single_double",  etc...
        const predefinedLayoutNames = Object.keys(ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS);
        if (typeof settings.pageLayout === 'string' && !ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS[settings.pageLayout]) {
            return errorLogger?.logError({
                message: `"pageLayout" value must be one of the following predefined layouts [${predefinedLayoutNames.map(
                    (name) => {
                        return `"${name}"`;
                    }).join(', ')}]`,
                value: settings.pageLayout,
                path : [...configPath, 'pageLayout'],
            }) ?? false;
        }

        // If "pageLayout" is an object, make sure it has the correct settings
        if (isObjectLiteral(settings.pageLayout) && !isEntityDetailsPageLayout(settings.pageLayout, errorLogger, [...configPath, 'pageLayout'])) {
            return false;
        }
    }

    if (
        settings.pageLayoutCustomization !== undefined
        && !isCustomizedEntityDetailsPageLayout(settings.pageLayoutCustomization, errorLogger, [...configPath, 'pageLayoutCustomization'])
    ) {
        return false;
    }

    if (
        settings.componentsPlacement !== undefined
        && !isEntityDetailsCompsPlacement(settings.componentsPlacement, errorLogger, [...configPath, 'componentsPlacement'])
    ) {
        return false;
    }

    return true;
};



/**
 * The interface for EACH entity details view.
 * @param displayNameKey - The label to be shown in the Button/Tab which is used for switching views. This can be a translation key.
 * @param icon - The optional icon to be shown in the Button/Tab
 * @param isReportView - Whether or not this view is a report view. Default is true unless this is specifically set to false
 * @param timeControl - The name/object of the time control to use for the view
 * @param showEntityName - Whether or not to show the entity's name on the top of the page. The default is TRUE unless this is specifically set to false
 * @param layoutSettings - The settings for the view's layout and components placement
 */
export interface EntityClassViewSettings {
    readonly displayNameKey?: string | undefined;
    readonly icon?: string | undefined;
    readonly isReportView?: boolean | undefined;
    readonly timeControl?: TimeControlType | EntityDetailsTimeSpanControlSettings | TimeRangeControlSettings | FixedTimeSpanControlSettings | FixedTimeRangeControlSettings;
    readonly showEntityName?: boolean | undefined;
    readonly layoutSettings?: EntityClassLayoutSettings | undefined;
}


/**
 * Type guard for EntityClassViewSettings. EntityClassViewSettings is configured by agent/developer manually so we need to make sure
 * they provided valid settings.
 */
export const isEntityClassViewsSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityClassViewSettings => {
    const settings = obj as EntityClassViewSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.displayNameKey)) {
        return errorLogger?.logError({
            message: '"displayNameKey" must be undefined or a non-empty string.',
            value  : settings.displayNameKey,
            path   : [...configPath, 'displayNameKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.isReportView)) {
        return errorLogger?.logError({
            message: '"isReportView" must be undefined or a boolean.',
            value  : settings.isReportView,
            path   : [...configPath, 'isReportView'],
        }) ?? false;
    }

    if (settings.timeControl !== undefined) {
        if (typeof settings.timeControl !== 'string' && !isObjectLiteral(settings.timeControl)) {
            return errorLogger?.logError({
                message: '"timeControl" must be undefined, a non-empty string, or an Object',
                value  : settings.timeControl,
                path   : [...configPath, 'timeControl'],
            }) ?? false;
        }

        const controlType = typeof settings.timeControl === 'string' ? settings.timeControl : settings.timeControl.type;

        if (!isValidSettingStringValue(controlType, true, false, Object.values(TimeControlType))) {
            return errorLogger?.logError({
                message: typeof settings.timeControl === 'string'
                    ? `"timeControl" value must be one of the following values [${Object.values(TimeControlType).map((value) => {
                        return `"${value}`;
                    }).join(', ')}]`
                    : `"type" must be one of the following values [${Object.values(TimeControlType).map((value) => {
                        return `"${value}`;
                    }).join(', ')}]`,
                value: controlType,
                path : typeof settings.timeControl === 'string' ? [...configPath, 'timeControl'] : [...configPath, 'timeControl', 'type'],
            }) ?? false;
        }

        if (isObjectLiteral(settings.timeControl)) {
            if (
                settings.timeControl.type === TimeControlType.TIME_SPAN
                && !isEntityDetailsTimeSpanControlSettings(settings.timeControl, errorLogger, [...configPath, 'timeControl'])
            ) {
                return false;
            }

            if (
                settings.timeControl.type === TimeControlType.FIXED_TIME_SPAN
                && !isFixedTimeSpanControlSettings(settings.timeControl, errorLogger, [...configPath, 'timeControl'])
            ) {
                return false;
            }

            if (
                settings.timeControl.type === TimeControlType.TIME_RANGE
                && !isTimeRangeControlSettings(settings.timeControl, errorLogger, [...configPath, 'timeControl'])
            ) {
                return false;
            }

            if (
                settings.timeControl.type === TimeControlType.FIXED_TIME_RANGE
                && !isFixedTimeRangeControlSettings(settings.timeControl, errorLogger, [...configPath, 'timeControl'])
            ) {
                return false;
            }
        }
    }

    if (!isValidSettingBooleanValue(settings.showEntityName)) {
        return errorLogger?.logError({
            message: '"showEntityName" must be undefined or a boolean.',
            value  : settings.showEntityName,
            path   : [...configPath, 'showEntityName'],
        }) ?? false;
    }

    if (
        settings.layoutSettings !== undefined
        && !isEntityClassLayoutSettings(settings.layoutSettings, errorLogger, [...configPath, 'layoutSettings'])
    ) {
        return false;
    }

    return true;
};



/**
 * Different entity classes store GPS coordinate in different ways. Some store entity GPS coordinates in entity's attributes and some store
 * them using TSDB, Event Logs, etc...
 */
export enum ENTITY_GPS_DATA_SOURCE {
    ATTRIBUTES = 'attributes',
    METRICS = 'metrics',
}


/**
 * Default attribute names we will be using for storing entity's GPS coordinate. These are used for immobile entities, entities that don't
 * move around and have fixed position.
 * TODO - Maybe consider creating a standard for all entity classes so that all entities use the same attribute names
 */
export const DEFAULT_ENTITY_GPS_ATTRIBUTE_NAME_LATITUDE = 'gps_lat';
export const DEFAULT_ENTITY_GPS_ATTRIBUTE_NAME_LONGITUDE = 'gps_lng';


/**
  * For mobile entities, entities that move around, we can use metrics to store the entity's GPS coordinate. We will create a separate metrics
  * definition where all the coordinates are stored. The coordinate e.g. latitude, longitude, will be stored under these collection
  * valueNames "gps_lat", "gps_lon"
  * TODO - Maybe consider creating a standard for all entity classes so that all entities use the same metrics definition name and
  * valueNames for GPS coordinates
*/
export const DEFAULT_ENTITY_GPS_METRICS_DEFINITION_ID = 'gps_metrics';
export const DEFAULT_ENTITY_GPS_METRICS_NAME_LATITUDE = 'gps_lat';
export const DEFAULT_ENTITY_GPS_METRICS_NAME_LONGITUDE = 'gps_lng';



/**
 * Base interface for entity GPS coordination storage configuration. Each entity class can store GPS coordinate in different data source.
 * Some store them in entity instance's attributes. Some store them using metrics. This interface is used for creating configuration that tell
 * the components where the GPS position is stored for an entity.
 */
export interface EntityGPSDataSourceSettings {
    readonly type: ENTITY_GPS_DATA_SOURCE;
}

/**
 * This interface is used for configuring Entities that use Attributes to store GPS coordinates. For these entities, we will need to know the
 * name of the attributes that are used for storing latitude and longitude.
 */
export interface EntityGPSDataSourceSettingsWithAttributes {
    readonly type: ENTITY_GPS_DATA_SOURCE.ATTRIBUTES;

    // Default names are ENTITY_GPS_ATTRIBUTE_NAME_LATITUDE and ENTITY_GPS_ATTRIBUTE_NAME_LONGITUDE
    readonly latAttrName?: string | undefined;
    readonly lngAttrName?: string | undefined;
}


/**
 * This interface is used for configuring Entities that use metrics to store GPS coordinates. For these entities, we will need to know the
 * metricsDefinitionId of the metrics definition where the GPS coordinates are stored. We also need the collection valueNames that are used for
 * storing latitude and longitude.
 */
export interface EntityGPSDataSourceSettingsWithMetrics {
    readonly type: ENTITY_GPS_DATA_SOURCE.METRICS;

    // Default names is ENTITY_GPS_METRICS_DEFINITION_NAME
    readonly metricsDefinitionId?: string | undefined;

    // Default names are ENTITY_GPS_METRICS_NAME_LATITUDE and ENTITY_GPS_METRICS_NAME_LONGITUDE
    readonly latMetricName?: string | undefined;
    readonly lngMetricName?: string | undefined;
}


/**
 * Type guard functions to check if an object is a specific type of EntityGPSDataSourceSettings
 */
export const isGPSDataSourceSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityGPSDataSourceSettings => {
    const settings = obj as EntityGPSDataSourceSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.type, true, false, Object.values(ENTITY_GPS_DATA_SOURCE))) {
        return errorLogger?.logError({
            message: `"type" must be one of the following values [${Object.values(ENTITY_GPS_DATA_SOURCE).map((value) => {
                return `"${value}"`;
            }).join(', ')}]`,
            value: settings.type,
            path : [...configPath, 'type'],
        }) ?? false;
    }

    return true;
};


/**
 * Type guard functions to check if an object is a specific type of EntityGPSDataSourceSettingsWithAttributes
 */
export const isEntityGPSDataSourceSettingsWithAttributes = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityGPSDataSourceSettingsWithAttributes => {
    const settings = obj as EntityGPSDataSourceSettingsWithAttributes;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isGPSDataSourceSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    if (settings.type !== ENTITY_GPS_DATA_SOURCE.ATTRIBUTES) {
        return errorLogger?.logError({
            message: `"type" must be "${ENTITY_GPS_DATA_SOURCE.ATTRIBUTES}".`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.latAttrName)) {
        return errorLogger?.logError({
            message: '"latAttrName" must be undefined or non-empty string.',
            value  : settings.latAttrName,
            path   : [...configPath, 'latAttrName'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.lngAttrName)) {
        return errorLogger?.logError({
            message: '"lngAttrName" must be undefined or non-empty string.',
            value  : settings.lngAttrName,
            path   : [...configPath, 'lngAttrName'],
        }) ?? false;
    }

    return true;
};


/**
 * Type guard functions to check if an object is a specific type of EntityGPSDataSourceSettingsWithMetrics
 */
export const isEntityGPSDataSourceSettingsWithMetrics = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityGPSDataSourceSettingsWithMetrics => {
    const settings = obj as EntityGPSDataSourceSettingsWithMetrics;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isGPSDataSourceSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    if (settings.type !== ENTITY_GPS_DATA_SOURCE.METRICS) {
        return errorLogger?.logError({
            message: `"type" must be "${ENTITY_GPS_DATA_SOURCE.METRICS}".`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.metricsDefinitionId)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" must be undefined or non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.latMetricName)) {
        return errorLogger?.logError({
            message: '"latMetricName" must be undefined or non-empty string.',
            value  : settings.latMetricName,
            path   : [...configPath, 'latMetricName'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.lngMetricName)) {
        return errorLogger?.logError({
            message: '"lngMetricName" must be undefined or non-empty string.',
            value  : settings.lngMetricName,
            path   : [...configPath, 'lngMetricName'],
        }) ?? false;
    }

    return true;
};



/**
 * Structure for entity class UI Settings. Any entity class needs to customize the UI can add this "uiSettings" to its' displaySettings props.
 * For example:
 *      entityClass: {
 *          displaySettings: {
 *              uiSettings: {
 *                  viewsSettings: {
 *                      // See isEntityClassViewsSettings
 *                  }
 *              },
 *              gpsDataSourceSettings: {
 *                  type: "attributes",
 *                  latAttrName: "gps_lat",
 *                  latAttrName: "gps_lng"
 *              }
 *          }
 *      }
 */
export interface EntityClassUISettings {
    readonly viewsSettings?: readonly EntityClassViewSettings[] | undefined;
    readonly gpsDataSourceSettings?: EntityGPSDataSourceSettingsWithAttributes | EntityGPSDataSourceSettingsWithMetrics | undefined;
}


/**
 * Type guard function to check if an entity class's uiSettings is valid. These settings are manually added to entity class therefore
 * we need to validate these objects before using them.
 */
export const isEntityClassUISettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityClassUISettings => {
    const settings = obj as EntityClassUISettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (settings.viewsSettings !== undefined) {
        if (!(settings.viewsSettings instanceof Array) || settings.viewsSettings.length <= 0) {
            return errorLogger?.logError({
                message: '"viewsSettings" must be undefined or an Array of View Setting objects',
                value  : settings.viewsSettings,
                path   : [...configPath, 'viewsSettings'],
            }) ?? false;
        }

        // Make sure every values in viewSettings are valid
        if (!settings.viewsSettings.every((viewSettings, index) => {
            return isEntityClassViewsSettings(viewSettings, errorLogger, [...configPath, 'viewsSettings', `[${index}]`]);
        })) {
            return false;
        }
    }

    if (settings.gpsDataSourceSettings !== undefined) {
        if (!isObjectLiteral(settings.gpsDataSourceSettings)) {
            return errorLogger?.logError({
                message: '"gpsDataSourceSettings" must be undefined or an object.',
                value  : settings.gpsDataSourceSettings,
                path   : [...configPath, 'gpsDataSourceSettings'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.gpsDataSourceSettings.type, true, false, Object.values(ENTITY_GPS_DATA_SOURCE))) {
            return errorLogger?.logError({
                message: `"type" is required and must be one of the following values [${Object.values(ENTITY_GPS_DATA_SOURCE).map((value) => {
                    return `"${value}"`;
                }).join(', ')}]`,
                value: settings.gpsDataSourceSettings.type,
                path : [...configPath, 'gpsDataSourceSettings', 'type'],
            }) ?? false;
        }

        if (
            settings.gpsDataSourceSettings.type === ENTITY_GPS_DATA_SOURCE.ATTRIBUTES
            && !isEntityGPSDataSourceSettingsWithAttributes(settings.gpsDataSourceSettings, errorLogger, [...configPath, 'gpsDataSourceSettings'])
        ) {
            return false;
        }

        if (
            settings.gpsDataSourceSettings.type === ENTITY_GPS_DATA_SOURCE.METRICS
            && !isEntityGPSDataSourceSettingsWithMetrics(settings.gpsDataSourceSettings, errorLogger, [...configPath, 'gpsDataSourceSettings'])
        ) {
            return false;
        }
    }

    return true;
};



/**
 * Interface for individual numeric metric customization. Each numeric metric info already has some attributes such as name, displayName,
 * unit. Sometimes we want to add additional attribute to the numeric metric therefore, this interface can be used for customizing
 * an individual numeric metric
 */
export interface NumericMetricSettings {
    // The number of decimal places to round the numeric metric value to
    readonly decimalPlaces?: number | undefined;

    // This option allows the admin to configure a component to transform/convert metric data to some other value. For example, this can
    // be used for converting temperature values from Celsius to Fahrenheit
    readonly transform?: {
        /**
         * The formula to use to transform the metric value. This formula can contain variables and the variables can be any of the other
         * metrics in the same collection or another collection from the same entity class. Use the metric name and metrics definition ID defined
         * in the metrics definition as variable names. Also, the variable {{value}} refer to the metric that is being transformed. Here are some
         * example:
         * Example 1: Convert temperature metric value from Celsius to Fahrenheit
         *      formula = "({{value}} × 9/5) + 32"
         *
         * Example 2: Find the http_errors in percent relative to total_errors. "http_errors" and "total_errors" are 2 metrics and "http_errors"
         *            is the metric we want to transform. And round the value to the nearest integer. NOTE: we can use Javascript functions
         *            and we don't need to use {{value}} to refer to the metric being transformed. We can use the name of the metric itself.
         *      formula = "Math.floor(({{http_errors}} / {{total_errors}}) * 100)
         *
         * Example 3: Find average temperature from all the metrics definitions. We can refer to metrics from another metrics definition using
         * by combining metricsDefinition with metric name separated by a "."
         *      formula = "({{environment.temperature}} + {{system.temperature}} + {{room.temperature}}) / 3"
         */
        readonly formula: string;

        // To override the metric's displayName and unit. When we transform a metric's values, it is most likely that the values are no longer
        // have the same meaning. So the admin can use this to override the data's name and unit if they need to.
        readonly displayNameKey?: string | undefined;
        readonly unitKey?: string | undefined;
    } | undefined;

    // Each numeric metric can have its' own custom chartType. This will override the global chartType.
    readonly chartType?: BasicXYChart | undefined;

    // The group name of the Y-Axis group to group the metrics with so that multiple metrics can share the same Y Axis. For example, There are 5
    // metrics and 3 of the metrics are of similar type, they all temperature measurements, and we want to use the same Y-Axis for these 3 metrics
    // and the other 2 metrics to have their own different Y-Axes. We can give the 3 metrics the same "yAxisGroupName", any unique string
    // within the chart.
    readonly yAxisGroupName?: string | undefined;

    // Add other settings as needed
}


/**
 * type guard function to check if an object is a type of NumericMetricSettings
 */
const isNumericMetricSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is NumericMetricSettings => {
    const settings = obj as NumericMetricSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // decimalPlaces is optional but if it is provided, it must be a integer between 0 and 10
    if (!isValidSettingNumberValue(settings.decimalPlaces, false, false, 0, 10)) {
        return errorLogger?.logError({
            message: '"decimalPlaces" must be undefined or not an integer between 0 and 10',
            value  : settings.decimalPlaces,
            path   : [...configPath, 'decimalPlaces'],
        }) ?? false;
    }

    if (settings.transform !== undefined) {
        if (!isObjectLiteral(settings.transform)) {
            return errorLogger?.logError({
                message: 'Value must be an object.',
                value  : settings.transform,
                path   : [...configPath, 'transform'],
            }) ?? false;
        }

        // "formula" is required and must be a string
        if (!isValidSettingStringValue(settings.transform.formula, true, false)) {
            return errorLogger?.logError({
                message: '"formula" is required and must be a non-empty string',
                value  : settings.transform.formula,
                path   : [...configPath, 'transform', 'formula'],
            }) ?? false;
        }

        // Make sure "formula has the correct format. It can have variables/placeholders and they must be in 1 of these 2 formats
        // {{temperature}} or {{environment.temperature}}
        const placeholders: string[] = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const match of settings.transform.formula.matchAll(/{{(.*?)}}/g)) {
            const placeholder = match[0];
            // If placeholder is not already added to the array then add it. We don't need to keep track of multiple same placeholders.
            // It is possible that a formula uses the same placeholder twice e.g. formula = "{{temperature}} * {{temperature}} + 5"
            // Also, ignore the {{value}} placeholders, we will handle it separately.
            if (!placeholders.includes(placeholder) && !(/{{\s*value\s*}}/.test(placeholder))) {
                placeholders.push(placeholder);
            }
        }

        if (!placeholders.every((placeholder) => {
            // remove the {{ }}
            const placeholderValue = placeholder.replace(/[{}]/g, '');

            // make sure the placeholderValue is either one of these 2 "temperature" or "environment.temperature", 1 string or 2 strings connected
            // by a period AND ALL strings is non-empty
            const parts = placeholderValue.split('.');

            if (!((parts.length === 1 || parts.length === 2) && parts.every((part) => {
                return isValidSettingStringValue(part, true, false);
            }))) {
                return errorLogger?.logError({
                    message: `"formula" contains invalid variables/placeholders, "${placeholder}"`,
                    value  : settings.transform?.formula,
                    path   : [...configPath, 'transform', 'formula'],
                }) ?? false;
            }

            return true;
        })) {
            return false;
        }


        // "displayNameKey" and "unitKey" are optional
        if (!isValidSettingStringValue(settings.transform.displayNameKey)) {
            return errorLogger?.logError({
                message: '"displayNameKey" must be undefined or a non-empty string',
                value  : settings.transform.displayNameKey,
                path   : [...configPath, 'transform', 'displayNameKey'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.transform.unitKey)) {
            return errorLogger?.logError({
                message: '"unitKey" must be undefined or a non-empty string',
                value  : settings.transform.unitKey,
                path   : [...configPath, 'transform', 'unitKey'],
            }) ?? false;
        }
    }


    // Validate settings.chartType
    if (!isValidSettingStringValue(settings.chartType, false, false, BASIC_XY_CHART_TYPES.map((type) => {
        return type as string;
    }))) {
        return errorLogger?.logError({
            message: `"chartType" must be undefined or one of the following [${
                Object.values(BASIC_XY_CHART_TYPES).map((chartType) => {
                    return `"${chartType}"`;
                }).join(', ')
            }].`,
            value: settings.chartType,
            path : [...configPath, 'chartType'],
        }) ?? false;
    }


    // yAxisGroupName is optional but if it is provided, it must be a non-empty string
    if (!isValidSettingStringValue(settings.yAxisGroupName, false, false)) {
        return errorLogger?.logError({
            message: '"yAxisGroupName" must be undefined or a non-empty string',
            value  : settings.yAxisGroupName,
            path   : [...configPath, 'yAxisGroupName'],
        }) ?? false;
    }

    return true;
};


/**
 * Some UI might need custom settings per numeric metric therefore, we will define this interface for any entity UI component to
 * extends if it needs custom metric settings
 */
export interface EntityNumericMetricsCustomSettings {

    // If we need custom settings for numeric metrics and we want to apply it too all numeric metrics, use this settings.
    readonly globalNumericMetricSettings?: NumericMetricSettings | undefined;

    /**
     * Customization per numeric metric. This allows the admin to customize individual metric to override the globalNumericMetricSettings.
     * Since numeric metrics are grouped in metric definition, this structure has 2 levels. For example
     *
     *  numericMetricSettingsPerMetricsDefinition = {
     *      environmentMetrics: {
     *          temperature: {...},
     *          pressure: {...}
     *      },
     *      internalMetrics: {
     *          batteryLevel: {...}
     *          cpuUsage: {...}
     *      },
     *  }
     */
    readonly numericMetricSettingsPerMetricsDefinition?: {
        readonly [metricsDefinitionId: string]: {
            readonly [metricName: string]: NumericMetricSettings | undefined;
        } | undefined;
    } | undefined;
}


/**
 * type guard function to check if an object is a type of EntityNumericMetricsCustomSettings
 */
export const isEntityNumericMetricsCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityNumericMetricsCustomSettings => {
    const settings = obj as EntityNumericMetricsCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // globalNumericMetricSettings can be undefined or an object of type NumericMetricSettings
    if (settings.globalNumericMetricSettings !== undefined) {
        // Make sure "globalNumericMetricSettings" is an object
        if (!isObjectLiteral(settings.globalNumericMetricSettings)) {
            return errorLogger?.logError({
                message: '"globalNumericMetricSettings" must be undefined or an object.',
                value  : settings.globalNumericMetricSettings,
                path   : [...configPath, 'globalNumericMetricSettings'],
            }) ?? false;
        }

        // Now check if it is a value NumericMetricSettings
        if (!isNumericMetricSettings(settings.globalNumericMetricSettings, errorLogger, [...configPath, 'globalNumericMetricSettings'])) {
            return false;
        }
    }


    // numericMetricSettingsPerMetricsDefinition can be undefined or an object that contain settings for each metric
    if (settings.numericMetricSettingsPerMetricsDefinition !== undefined) {
        // Make sure "numericMetricSettingsPerMetricsDefinition" is an object
        if (!isObjectLiteral(settings.numericMetricSettingsPerMetricsDefinition)) {
            return errorLogger?.logError({
                message: '"numericMetricSettingsPerMetricsDefinition" must be undefined or an object.',
                value  : settings.numericMetricSettingsPerMetricsDefinition,
                path   : [...configPath, 'numericMetricSettingsPerMetricsDefinition'],
            }) ?? false;
        }

        // Now make sure each values in the numericMetricSettingsPerMetricsDefinition is an object of NumericMetricSettings
        if (!Object.entries(settings.numericMetricSettingsPerMetricsDefinition).every(([metricsDefId, settingsPerMetricsDefinition]) => {
            if (!isObjectLiteral(settingsPerMetricsDefinition)) {
                return errorLogger?.logError({
                    message: `"${metricsDefId}" must be an object.`,
                    value  : settingsPerMetricsDefinition,
                    path   : [...configPath, 'numericMetricSettingsPerMetricsDefinition', `${metricsDefId}`],
                }) ?? false;
            }

            return Object.entries(settingsPerMetricsDefinition).every(([metricName, numericMetricSettings]) => {
                return isNumericMetricSettings(
                    numericMetricSettings,
                    errorLogger,
                    [...configPath, 'numericMetricSettingsPerMetricsDefinition', metricsDefId, metricName],
                );
            });
        })) {
            return false;
        }
    }

    return true;
};


/**
 * Helper function to get the NumericMetricSettings for a given metricsDefinitionId and metricName.
 * This function will fist try to find the NumericMetricSettings from the "numericMetricSettingsPerMetricsDefinition" for specific
 * settings. If it is not found then this function will return the globalNumericMetricSettings
 */
export const getNumericMetricCustomSettings = (
    metricName: string,
    metricsDefinitionId?: string,
    customSettings?: EntityNumericMetricsCustomSettings,
): NumericMetricSettings | undefined => {
    if (customSettings) {
        const settingsPerMetricsDef = metricsDefinitionId ? customSettings.numericMetricSettingsPerMetricsDefinition?.[metricsDefinitionId] : undefined;
        if (settingsPerMetricsDef && settingsPerMetricsDef[metricName]) {
            // If there is a specific settings for the provided metricsDefinitionId then use that
            return settingsPerMetricsDef[metricName];
        }

        // Return the global settings for all numeric metrics
        return customSettings.globalNumericMetricSettings;
    }
    return undefined;
};



/**
 * Many UI Components' customSettings will have these settings to exclude some entity classes in the view therefore we will create an interface
 * for these settings and provide type guard function to validate them. This way we don't need to repeat this every where.
 */
export interface EntityClassesExclusionCustomSettings {
    // To INCLUDE some specific entity classes. By default, this will include entity instances of all entity classes unless this option is provided.
    // This must be an ARRAY of entity classes that we want to INCLUDE.
    readonly includeEntityClasses?: readonly string[] | undefined;

    // To EXCLUDE some specific entity classes. By default, this will NOT exclude any children unless this option is provided.
    // This must be an ARRAY of entity classes that we want to EXCLUDE.
    readonly excludeEntityClasses?: readonly string[] | undefined;
}

// Type guard function to validate a EntityClassesExclusionCustomSettings object
export const isEntityClassesExclusionCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityClassesExclusionCustomSettings => {
    const settings = obj as EntityClassesExclusionCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // includeEntityClasses can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.includeEntityClasses !== undefined) {
        if (!(settings.includeEntityClasses instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeEntityClasses" must be undefined or an array of strings.',
                value  : settings.includeEntityClasses,
                path   : [...configPath, 'includeEntityClasses'],
            }) ?? false;
        }

        // Return false if some of the items in the array is NOT a string
        if (!settings.includeEntityClasses.every((entityClass, index) => {
            if (!isValidSettingStringValue(entityClass, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "includeEntityClasses" array must be non-empty strings',
                    value  : entityClass,
                    path   : [...configPath, 'includeEntityClasses', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // excludeEntityClasses can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.excludeEntityClasses !== undefined) {
        if (!(settings.excludeEntityClasses instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeEntityClasses" must be undefined or an array of strings.',
                value  : settings.excludeEntityClasses,
                path   : [...configPath, 'excludeEntityClasses'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.excludeEntityClasses.every((entityClass, index) => {
            if (!isValidSettingStringValue(entityClass, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "excludeEntityClasses" array must be non-empty strings',
                    value  : entityClass,
                    path   : [...configPath, 'excludeEntityClasses', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // All checks passed
    return true;
};



/**
 * Many UI Components' customSettings will have these settings to exclude some entity instanced in the view therefore we will create an interface
 * for these settings and provide type guard function to validate them. This way we don't need to repeat this every where.
 */
export interface EntityInstanceExclusionCustomSettings {
    // To INCLUDE some specific entity instances. By default, this will include all entity instances unless this option is provided.
    // This must be an ARRAY of entity instance IDs that we want to INCLUDE.
    readonly includeEntityIds?: readonly string[] | undefined;

    // To EXCLUDE some specific entity instances. By default, this will NOT exclude any instances unless this option is provided.
    // This must be an ARRAY of entity instance IDs that we want to EXCLUDE.
    readonly excludeEntityIds?: readonly string[] | undefined;
}


// Type guard function to validate a EntityInstanceExclusionCustomSettings object
export const isEntityInstancesExclusionCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityInstanceExclusionCustomSettings => {
    const settings = obj as EntityInstanceExclusionCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // includeEntityIds can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.includeEntityIds !== undefined) {
        if (!(settings.includeEntityIds instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeEntityIds" must be undefined or an array of strings.',
                value  : settings.includeEntityIds,
                path   : [...configPath, 'includeEntityIds'],
            }) ?? false;
        }

        // Return false if some of the items in the array is NOT a string
        if (!settings.includeEntityIds.every((entityId, index) => {
            if (!isValidSettingStringValue(entityId, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "includeEntityIds" array must be non-empty strings',
                    value  : entityId,
                    path   : [...configPath, 'includeEntityIds', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // excludeEntityIds can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.excludeEntityIds !== undefined) {
        if (!(settings.excludeEntityIds instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeEntityIds" must be undefined or an array of strings.',
                value  : settings.excludeEntityIds,
                path   : [...configPath, 'excludeEntityIds'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.excludeEntityIds.every((entityId, index) => {
            if (!isValidSettingStringValue(entityId)) {
                return errorLogger?.logError({
                    message: 'Every values in "excludeEntityIds" array must be non-empty strings',
                    value  : entityId,
                    path   : [...configPath, 'excludeEntityIds', `[${index}]`],
                }) ?? false;
            }

            return true;
        })) {
            return false;
        }
    }

    // All checks passed
    return true;
};



/**
 * Combine both "EntityClassesExclusionCustomSettings" and "EntityInstanceExclusionCustomSettings" into 1 interface.
 * For EntityUIComponent that wants to be able to exclude/include both entity classes and entity instances can extends this interface
 */
export interface EntityExclusionCustomSettings extends EntityClassesExclusionCustomSettings, EntityInstanceExclusionCustomSettings {
}


/**
  * Type guard function to make sure an object is a type of EntityExclusionCustomSettings. This function will
  * just going to call the "isEntityClassesExclusionCustomSettings" and "isEntityInstancesExclusionCustomSettings" to validate the object
  * since the object extends both interfaces
  */
export const isEntityExclusionCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityExclusionCustomSettings => {
    return isEntityClassesExclusionCustomSettings(obj, errorLogger, currentConfigPath)
        && isEntityInstancesExclusionCustomSettings(obj, errorLogger, currentConfigPath);
};



/**
 * Given an array of entities, filter out entities base on the entityClassesExclusionSettings and return the array of
 * entities that satisfy the conditions in entityClassesExclusionSettings
 */
export const filterEntityClassesByEntityClassesExclusionCustomSettings = (
    childEntities: readonly Entity[],
    entityClassesExclusionSettings: EntityClassesExclusionCustomSettings | undefined,
): readonly Entity[] => {
    const includeEntityClasses = entityClassesExclusionSettings?.includeEntityClasses;
    const excludeEntityClasses = entityClassesExclusionSettings?.excludeEntityClasses;

    if (includeEntityClasses || excludeEntityClasses) {
        return childEntities.filter((entity) => {
            if (includeEntityClasses && !includeEntityClasses.includes(entity.entityClass)) {
                return false;
            }
            if (excludeEntityClasses && excludeEntityClasses.includes(entity.entityClass)) {
                return false;
            }
            return true;
        });
    }

    // There are no exclude/include entity classes settings, return the childEntities array as is
    return childEntities;
};


/**
 * Given an array of entities, filter out entities base on the EntityInstanceExclusionCustomSettings and return the array of
 * entities that satisfy the conditions in EntityInstanceExclusionCustomSettings
 */
export const filterEntityInstancesByEntityInstancesExclusionCustomSettings = (
    childEntities: readonly Entity[],
    entityInstancesExclusionSettings: EntityInstanceExclusionCustomSettings | undefined,
): readonly Entity[] => {
    const includeEntityIds = entityInstancesExclusionSettings?.includeEntityIds;
    const excludeEntityIds = entityInstancesExclusionSettings?.excludeEntityIds;

    if (includeEntityIds || excludeEntityIds) {
        return childEntities.filter((entity) => {
            if (includeEntityIds && !includeEntityIds.includes(entity.entityId)) {
                return false;
            }
            if (excludeEntityIds && excludeEntityIds.includes(entity.entityId)) {
                return false;
            }
            return true;
        });
    }

    // There are no exclude/include entity instances settings, return the childEntities array as is
    return childEntities;
};

/**
 * Many UI Components' customSettings will have these settings to exclude some metrics definition in the view therefore we will create an interface
 * for these settings and provide type guard function to validate them. This way we don't need to repeat this every where.
 */
export interface MetricsDefinitionsExclusionCustomSettings {

    // The array of metric definition IDs we want to exclude
    readonly excludeMetricsDefinitionIds?: readonly string[] | undefined;

    // The array of metric definition IDs we want to include. If there are many metricsDefinitions and we only want to display 1 and exclude the
    // rest, it would be easier to use this 'includeMetricsDefinitionIds' to specify the metrics definition we want to display instead of
    // using the 'excludeMetricsDefinitionIds' to specify the metrics definition we want to exclude.
    readonly includeMetricsDefinitionIds?: readonly string[] | undefined;

    // The array of data source module type to exclude e.g. 'tsdb', 'video', etc...
    readonly excludeDataSourceModuleTypes?: readonly (SmartModuleType | EntityModuleType)[] | undefined;

    // The array of data source module type to include e.g. 'tsdb', 'video', etc...
    readonly includeDataSourceModuleTypes?: readonly (SmartModuleType | EntityModuleType)[] | undefined;

    // The array of metrics types to exclude e.g. 'raw', 'derived', etc...
    readonly excludeMetricsTypes?: readonly MetricType [] | undefined;

    // The array of metrics types to include e.g. 'raw', 'derived', etc...
    readonly includeMetricsTypes?: readonly MetricType [] | undefined;
}


// Type guard function to validate a MetricsDefinitionsExclusionCustomSettings object
export const isMetricsDefinitionsExclusionCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is MetricsDefinitionsExclusionCustomSettings => {
    const settings = obj as MetricsDefinitionsExclusionCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // excludeMetricsDefinitionIds can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.excludeMetricsDefinitionIds !== undefined) {
        if (!(settings.excludeMetricsDefinitionIds instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeMetricsDefinitionIds" must be undefined or an array of strings.',
                value  : settings.excludeMetricsDefinitionIds,
                path   : [...configPath, 'excludeMetricsDefinitionIds'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.excludeMetricsDefinitionIds.every((metricDefId, index) => {
            if (!isValidSettingStringValue(metricDefId)) {
                return errorLogger?.logError({
                    message: 'Every values in "excludeMetricsDefinitionIds" array must be non-empty strings',
                    value  : metricDefId,
                    path   : [...configPath, 'excludeMetricsDefinitionIds', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // includeMetricsDefinitionIds can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.includeMetricsDefinitionIds !== undefined) {
        if (!(settings.includeMetricsDefinitionIds instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeMetricsDefinitionIds" must be undefined or an Array of strings.',
                value  : settings.includeMetricsDefinitionIds,
                path   : [...configPath, 'includeMetricsDefinitionIds'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.includeMetricsDefinitionIds.every((metricDefId, index) => {
            if (!isValidSettingStringValue(metricDefId)) {
                return errorLogger?.logError({
                    message: 'Every values in "includeMetricsDefinitionIds" array must be non-empty strings',
                    value  : metricDefId,
                    path   : [...configPath, 'includeMetricsDefinitionIds', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // excludeDataSourceModuleTypes can be undefined or empty array. But if it is an array, it must be an array of SmartModuleType
    // e.g. 'tsdb', 'video', etc...
    if (settings.excludeDataSourceModuleTypes !== undefined) {
        if (!(settings.excludeDataSourceModuleTypes instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeDataSourceModuleTypes" must be undefined or an Array of strings.',
                value  : settings.excludeDataSourceModuleTypes,
                path   : [...configPath, 'excludeDataSourceModuleTypes'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a SmartModuleType
        const moduleTypes = [...Object.values(SmartModuleType), ...Object.values(EntityModuleType)];
        if (!settings.excludeDataSourceModuleTypes.every((type, index) => {
            if (!moduleTypes.includes(type)) {
                return errorLogger?.logError({
                    message: `Every values in "excludeDataSourceModuleTypes" array must be of these values [${moduleTypes.map((value) => {
                        return `"${value}"`;
                    }).join(', ')}]`,
                    value: type,
                    path : [...configPath, 'excludeDataSourceModuleTypes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // includeDataSourceModuleTypes can be undefined or empty array. But if it is an array, it must be an array of SmartModuleType
    // e.g. 'tsdb', 'video', etc...
    if (settings.includeDataSourceModuleTypes !== undefined) {
        if (!(settings.includeDataSourceModuleTypes instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeDataSourceModuleTypes" must be undefined or an Array of strings.',
                value  : settings.includeDataSourceModuleTypes,
                path   : [...configPath, 'includeDataSourceModuleTypes'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a SmartModuleType
        const moduleTypes = [...Object.values(SmartModuleType), ...Object.values(EntityModuleType)];
        if (!settings.includeDataSourceModuleTypes.every((type, index) => {
            if (!moduleTypes.includes(type)) {
                return errorLogger?.logError({
                    message: `Every values in "includeDataSourceModuleTypes" array must be one of the following values [${moduleTypes.map((value) => {
                        return `"${value}"`;
                    }).join(', ')}]`,
                    value: type,
                    path : [...configPath, 'includeDataSourceModuleTypes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // excludeMetricsTypes can be undefined or empty array. But if it is an array, it must be an array of MetricType e.g. 'raw', 'derived', etc...
    if (settings.excludeMetricsTypes !== undefined) {
        if (!(settings.excludeMetricsTypes instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeMetricsTypes" must be undefined or an Array of strings.',
                value  : settings.excludeMetricsTypes,
                path   : [...configPath, 'excludeMetricsTypes'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a MetricType
        const metricTypes = Object.values(MetricType);
        if (!settings.excludeMetricsTypes.every((type, index) => {
            if (!metricTypes.includes(type)) {
                return errorLogger?.logError({
                    message: `Every values in "excludeMetricsTypes" array must be one of the following values [${metricTypes.map((value) => {
                        return `"${value}"`;
                    }).join(', ')}]`,
                    value: type,
                    path : [...configPath, 'excludeMetricsTypes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // includeMetricsTypes can be undefined or empty array. But if it is an array, it must be an array of MetricType e.g. 'raw', 'derived', etc...
    if (settings.includeMetricsTypes !== undefined) {
        if (!(settings.includeMetricsTypes instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeMetricsTypes" must be undefined or an Array of strings.',
                value  : settings.includeMetricsTypes,
                path   : [...configPath, 'includeMetricsTypes'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a MetricType
        const metricTypes = Object.values(MetricType);
        if (!settings.includeMetricsTypes.every((type, index) => {
            if (!metricTypes.includes(type)) {
                return errorLogger?.logError({
                    message: `Every values in "includeMetricsTypes" array must be one of these values [${metricTypes.map((value) => {
                        return `"${value}"`;
                    }).join(', ')}]`,
                    value: type,
                    path : [...configPath, 'includeMetricsTypes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // All checks passed
    return true;
};


/**
 * Many UI Components' customSettings will have these settings to exclude some metrics in the view therefore we will create an interface
 * for these settings and provide type guard function to validate them. This way we don't need to repeat this every where.
 */
export interface MetricsExclusionCustomSettings {
    // Whether or not to exclude some of the metrics. The default is FALSE and will show all metrics.
    readonly excludeNumericMetrics?: boolean | undefined;
    readonly excludeTagMetrics?: boolean | undefined;
    readonly excludeKPIMetrics?: boolean | undefined;
    readonly excludeRegularMetrics?: boolean | undefined;

    // The array of numeric metric name we want to exclude
    readonly excludeNumericMetricNames?: readonly string[] | undefined;

    // The array of numeric metric name we want to include
    readonly includeNumericMetricNames?: readonly string[] | undefined;

    // The array of tag metric name we want to exclude
    readonly excludeTagMetricNames?: readonly string[] | undefined;

    // The array of tag metric name we want to include
    readonly includeTagMetricNames?: readonly string[] | undefined;

    // To exclude some metrics per metric definition. These will only apply to a specific metric definition. If we want to exclude
    // tagMetrics for a specific metricsDefinitionId, we would use this instead of the 'excludeTagMetrics' defined above which excludes
    // ALL tagMetrics
    readonly perMetricsDefinitionSettings?: {
        readonly [metricsDefinitionId: string]: {
            readonly excludeNumericMetrics?: boolean | undefined;
            readonly excludeTagMetrics?: boolean | undefined;
            readonly excludeKPIMetrics?: boolean | undefined;
            readonly excludeRegularMetrics?: boolean | undefined;
            readonly excludeNumericMetricNames?: readonly string[] | undefined;
            readonly includeNumericMetricNames?: readonly string[] | undefined;
            readonly excludeTagMetricNames?: readonly string[] | undefined;
            readonly includeTagMetricNames?: readonly string[] | undefined;
        } | undefined;
    } | undefined;
}


// Type guard function to validate a MetricsExclusionCustomSettings object
export const isMetricsExclusionCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is MetricsExclusionCustomSettings => {
    const settings = obj as MetricsExclusionCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.excludeNumericMetrics)) {
        return errorLogger?.logError({
            message: '"excludeNumericMetrics" must be undefined or a boolean',
            value  : settings.excludeNumericMetrics,
            path   : [...configPath, 'excludeNumericMetrics'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.excludeTagMetrics)) {
        return errorLogger?.logError({
            message: '"excludeTagMetrics" must be undefined or a boolean',
            value  : settings.excludeTagMetrics,
            path   : [...configPath, 'excludeTagMetrics'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.excludeKPIMetrics)) {
        return errorLogger?.logError({
            message: '"excludeKPIMetrics" must be undefined or a boolean',
            value  : settings.excludeKPIMetrics,
            path   : [...configPath, 'excludeKPIMetrics'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.excludeRegularMetrics)) {
        return errorLogger?.logError({
            message: '"excludeRegularMetrics" must be undefined or a boolean',
            value  : settings.excludeRegularMetrics,
            path   : [...configPath, 'excludeRegularMetrics'],
        }) ?? false;
    }

    // excludeNumericMetricNames can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.excludeNumericMetricNames !== undefined) {
        if (!(settings.excludeNumericMetricNames instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeNumericMetricNames" must be undefined or an Array of strings.',
                value  : settings.excludeNumericMetricNames,
                path   : [...configPath, 'excludeNumericMetricNames'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.excludeNumericMetricNames.every((metricDefId, index) => {
            if (!isValidSettingStringValue(metricDefId)) {
                return errorLogger?.logError({
                    message: 'Every values in "excludeNumericMetricNames" array must be non-empty strings',
                    value  : metricDefId,
                    path   : [...configPath, 'excludeNumericMetricNames', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // includeNumericMetricNames can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.includeNumericMetricNames !== undefined) {
        if (!(settings.includeNumericMetricNames instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeNumericMetricNames" must be undefined or an Array of strings.',
                value  : settings.includeNumericMetricNames,
                path   : [...configPath, 'includeNumericMetricNames'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.includeNumericMetricNames.every((metricDefId, index) => {
            if (!isValidSettingStringValue(metricDefId)) {
                return errorLogger?.logError({
                    message: 'Every values in "includeNumericMetricNames" must be non-empty strings',
                    value  : metricDefId,
                    path   : [...configPath, 'includeNumericMetricNames', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // excludeTagMetricNames can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.excludeTagMetricNames !== undefined) {
        if (!(settings.excludeTagMetricNames instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeTagMetricNames" must be undefined or an Array of strings.',
                value  : settings.excludeTagMetricNames,
                path   : [...configPath, 'excludeTagMetricNames'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.excludeTagMetricNames.every((metricDefId, index) => {
            if (!isValidSettingStringValue(metricDefId)) {
                return errorLogger?.logError({
                    message: 'Every values in "excludeTagMetricNames" array must be non-empty strings',
                    value  : metricDefId,
                    path   : [...configPath, 'excludeTagMetricNames', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // includeTagMetricNames can be undefined or empty array. But if it is an array, it must be an array of strings
    if (settings.includeTagMetricNames !== undefined) {
        if (!(settings.includeTagMetricNames instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeTagMetricNames" must be undefined or an Array of strings.',
                value  : settings.includeTagMetricNames,
                path   : [...configPath, 'includeTagMetricNames'],
            }) ?? false;
        }
        // Return false if some of the items in the array is NOT a string
        if (!settings.includeTagMetricNames.every((metricDefId, index) => {
            if (!isValidSettingStringValue(metricDefId)) {
                return errorLogger?.logError({
                    message: 'Every values in "includeTagMetricNames" array must be non-empty strings',
                    value  : metricDefId,
                    path   : [...configPath, 'includeTagMetricNames', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // perMetricsDefinitionSettings can be undefined or an object. If it is an object of key/value, the key must be a metricDefinitionId and the
    // values must be an object that contain 4 of the 'excludeNumericMetrics', 'excludeTagMetrics', 'excludeKPIMetrics', etc...
    if (settings.perMetricsDefinitionSettings !== undefined) {
        if (!isObjectLiteral(settings.perMetricsDefinitionSettings)) {
            return errorLogger?.logError({
                message: '"perMetricsDefinitionSettings" must be undefined or an Object.',
                value  : settings.includeTagMetricNames,
                path   : [...configPath, 'perMetricsDefinitionSettings'],
            }) ?? false;
        }
        // Return false if NOT all of the values in the object are Objects that contain 4 of the exclude metrics flags
        if (!Object.entries(settings.perMetricsDefinitionSettings).every(([metricsDefId, value]) => {
            if (!isMetricsExclusionCustomSettings(value, errorLogger, [...configPath, 'perMetricsDefinitionSettings', `${metricsDefId}`])) {
                return false;
            }
            return true;
        })) {
            return false;
        }
    }

    // All checks passed
    return true;
};


/**
 * Combine both "MetricsDefinitionsExclusionCustomSettings" and "MetricsExclusionCustomSettings" into 1 interface.
 * For EntityUIComponent that wants to be able to exclude/include both metrics definitions and metrics can extends this interface
 */
export interface MetricsDefinitionsAndMetricsExclusionCustomSettings extends MetricsDefinitionsExclusionCustomSettings, MetricsExclusionCustomSettings {
}


/**
 * Type guard function to make sure an object is a type of MetricsDefinitionAndMetricsExclusionCustomSettings. This function will
 * just going to call the "isMetricsDefinitionsExclusionCustomSettings" and "isMetricsExclusionCustomSettings" to validate the object
 * since the object extends both interfaces
 */
export const isMetricsDefinitionAndMetricsExclusionCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is MetricsDefinitionsAndMetricsExclusionCustomSettings => {
    return isMetricsDefinitionsExclusionCustomSettings(obj, errorLogger, currentConfigPath)
        && isMetricsExclusionCustomSettings(obj, errorLogger, currentConfigPath);
};


/**
 * Filter an array of metrics definitions based on the specified MetricsExclusionCustomSettings.
 * All the UI component that extends the MetricsExclusionCustomSettings will need this function to filter out metrics definitions
 * so we will make this function reusable.
 */
export const filterMetricsDefinitionsByMetricsExclusionCustomSettings = (
    metricsDefinitions: readonly EntityMetricsDefinition[],
    customSettings: (MetricsDefinitionsExclusionCustomSettings & MetricsExclusionCustomSettings) | undefined,
): readonly EntityMetricsDefinition[] => {

    return metricsDefinitions.filter((metricDef) => {

        // If the current metricDef is in the list of `excludeMetricsDefinitionIds` then exclude it
        if (customSettings?.excludeMetricsDefinitionIds
            && customSettings.excludeMetricsDefinitionIds.includes(metricDef.metricsDefinitionId)) {
            return false;
        }

        // If the current metricDef is NOT in the list of `includeMetricsDefinitionIds` then exclude it
        if (customSettings?.includeMetricsDefinitionIds
            && !customSettings.includeMetricsDefinitionIds.includes(metricDef.metricsDefinitionId)) {
            return false;
        }

        // If the current metricDef.dataStoreConfiguration.moduleType is in the list of `excludeDataSourceModuleTypes` then exclude it
        if (customSettings?.excludeDataSourceModuleTypes
            && customSettings.excludeDataSourceModuleTypes.includes(metricDef.dataStoreConfiguration.moduleType)) {
            return false;
        }

        // If the current metricDef.includeDataSourceModuleTypes.moduleType is NOT in the list of `includeDataSourceModuleTypes` then exclude it
        if (customSettings?.includeDataSourceModuleTypes
            && !customSettings.includeDataSourceModuleTypes.includes(metricDef.dataStoreConfiguration.moduleType)) {
            return false;
        }

        // If the current metricDef.metricType is in the list of `excludeMetricsTypes` then exclude it
        if (customSettings?.excludeMetricsTypes
            && customSettings.excludeMetricsTypes.includes(metricDef.metricsType)) {
            return false;
        }

        // If the current metricDef.metricType is NOT in the list of `includeMetricsTypes` then exclude it
        if (customSettings?.includeMetricsTypes
            && !customSettings.includeMetricsTypes.includes(metricDef.metricsType)) {
            return false;
        }

        // If one or all 'excludeNumericMetrics', 'excludeTagMetrics', 'excludeKPIMetrics', 'excludeRegularMetrics' are set
        // We need to check check if there is any metrics from metricDef that are visible. If after applying all the
        // exclusion and there is no metric left to be shown for this metricDef, then we don't want to include this metricDef
        // either because it will end up showing an empty space.
        const perMetricsDefinitionSettings = customSettings?.perMetricsDefinitionSettings?.[metricDef.metricsDefinitionId];
        if (
            customSettings?.excludeNumericMetrics || customSettings?.excludeTagMetrics
            || customSettings?.excludeKPIMetrics || customSettings?.excludeRegularMetrics
            || customSettings?.excludeNumericMetricNames || customSettings?.includeNumericMetricNames
            || customSettings?.excludeTagMetricNames || customSettings?.includeTagMetricNames
            || perMetricsDefinitionSettings?.excludeNumericMetrics || perMetricsDefinitionSettings?.excludeTagMetrics
            || perMetricsDefinitionSettings?.excludeKPIMetrics || perMetricsDefinitionSettings?.excludeRegularMetrics
            || perMetricsDefinitionSettings?.excludeNumericMetricNames || perMetricsDefinitionSettings?.includeNumericMetricNames
            || perMetricsDefinitionSettings?.excludeTagMetricNames || perMetricsDefinitionSettings?.includeTagMetricNames
        ) {
            const visibleNumericMetrics = metricDef.numericMetrics?.filter((metric) => {
                if (isKPIMetric(metric)) {
                    if (customSettings?.excludeKPIMetrics || perMetricsDefinitionSettings?.excludeKPIMetrics) {
                        return false;
                    }
                } else if (customSettings?.excludeRegularMetrics || perMetricsDefinitionSettings?.excludeRegularMetrics) {
                    // if NOT KPI metric and we need to exclude regular metric then exclude this
                    return false;
                }
                if (customSettings?.excludeNumericMetrics || perMetricsDefinitionSettings?.excludeNumericMetrics) {
                    return false;
                }

                // If excludeNumericMetricNames is configured AND the metric name matches one of the excludeNumericMetricNames
                if (customSettings?.excludeNumericMetricNames && customSettings.excludeNumericMetricNames.includes(metric.name)) {
                    return false;
                }

                // If excludeNumericMetricNames is configured AND the metric name matches one of the excludeNumericMetricNames
                if (perMetricsDefinitionSettings?.excludeNumericMetricNames && perMetricsDefinitionSettings.excludeNumericMetricNames.includes(metric.name)) {
                    return false;
                }

                // If includeNumericMetricNames is configured BUT the metric name DOES NOT match one of the includeNumericMetricNames
                if (customSettings?.includeNumericMetricNames && !customSettings.includeNumericMetricNames.includes(metric.name)) {
                    return false;
                }
                if (perMetricsDefinitionSettings?.includeNumericMetricNames && !perMetricsDefinitionSettings.includeNumericMetricNames.includes(metric.name)) {
                    return false;
                }

                return true;
            }) ?? [];

            const visibleTagMetrics = metricDef.tagMetrics?.filter((metric) => {
                if (customSettings?.excludeRegularMetrics || perMetricsDefinitionSettings?.excludeRegularMetrics) {
                    return false;
                }
                if (customSettings?.excludeTagMetrics || perMetricsDefinitionSettings?.excludeTagMetrics) {
                    return false;
                }

                // If excludeTagMetricNames is configured AND the metric name matches one of the excludeTagMetricNames
                if (customSettings?.excludeTagMetricNames && customSettings.excludeTagMetricNames.includes(metric.name)) {
                    return false;
                }

                // If excludeTagMetricNames is configured AND the metric name matches one of the excludeTagMetricNames
                if (perMetricsDefinitionSettings?.excludeTagMetricNames && perMetricsDefinitionSettings.excludeTagMetricNames.includes(metric.name)) {
                    return false;
                }

                // If includeTagMetricNames is configured BUT the metric name DOES NOT match one of the includeTagMetricNames
                if (customSettings?.includeTagMetricNames && !customSettings.includeTagMetricNames.includes(metric.name)) {
                    return false;
                }

                // If includeTagMetricNames is configured BUT the metric name DOES NOT match one of the includeTagMetricNames
                if (perMetricsDefinitionSettings?.includeTagMetricNames && !perMetricsDefinitionSettings.includeTagMetricNames.includes(metric.name)) {
                    return false;
                }
                return true;
            }) ?? [];

            // If there is no numeric/tag metric visible then return false to exclude this metricDef as well.
            if (visibleNumericMetrics.length === 0 && visibleTagMetrics.length === 0) {
                return false;
            }
        }

        // Return true to INCLUDE this metricDef
        return true;
    });
};


/**
 * Filter an array of metrics based on the provided MetricsExclusionCustomSettings. All UI component that extends MetricsExclusionCustomSettings
 * will need a function to filter out excluded metrics so we will make this function reusable.
 */
export const filterMetricsByMetricsExclusionCustomSettings = <T extends NumericMetricInfo | TagMetricInfo>(
    metricDefinitionId: string,
    metrics: readonly T[] | undefined,
    customSettings: MetricsExclusionCustomSettings | undefined,
    areMetricsNumericMetrics: boolean,
): readonly T[] => {
    const perMetricsDefinitionSettings = customSettings?.perMetricsDefinitionSettings?.[metricDefinitionId];
    return metrics?.filter((metricInfo) => {
        if (isKPIMetric(metricInfo)) {
            if (customSettings?.excludeKPIMetrics || perMetricsDefinitionSettings?.excludeKPIMetrics) {
                return false;
            }
        } else if (customSettings?.excludeRegularMetrics || perMetricsDefinitionSettings?.excludeRegularMetrics) {
            // if NOT KPI metric and we need to exclude regular metric then exclude this
            return false;
        }

        if (customSettings?.excludeNumericMetricNames && customSettings.excludeNumericMetricNames.includes(metricInfo.name)) {
            return false;
        }
        if (perMetricsDefinitionSettings?.excludeNumericMetricNames && perMetricsDefinitionSettings.excludeNumericMetricNames.includes(metricInfo.name)) {
            return false;
        }
        if (customSettings?.includeNumericMetricNames && !customSettings.includeNumericMetricNames.includes(metricInfo.name)) {
            return false;
        }
        if (perMetricsDefinitionSettings?.includeNumericMetricNames && !perMetricsDefinitionSettings.includeNumericMetricNames.includes(metricInfo.name)) {
            return false;
        }

        // If the metrics are numeric metrics and we need to exclude numeric metrics then return false
        if ((customSettings?.excludeNumericMetrics || perMetricsDefinitionSettings?.excludeNumericMetrics) && areMetricsNumericMetrics) {
            return false;
        }

        // Same as exclude tag metrics
        if ((customSettings?.excludeTagMetrics || perMetricsDefinitionSettings?.excludeTagMetrics) && !areMetricsNumericMetrics) {
            return false;
        }

        if (customSettings?.excludeTagMetricNames && customSettings.excludeTagMetricNames.includes(metricInfo.name)) {
            return false;
        }
        if (perMetricsDefinitionSettings?.excludeTagMetricNames && perMetricsDefinitionSettings.excludeTagMetricNames.includes(metricInfo.name)) {
            return false;
        }
        if (customSettings?.includeTagMetricNames && !customSettings.includeTagMetricNames.includes(metricInfo.name)) {
            return false;
        }
        if (perMetricsDefinitionSettings?.includeTagMetricNames && !perMetricsDefinitionSettings.includeTagMetricNames.includes(metricInfo.name)) {
            return false;
        }

        return true;
    }) ?? [];
};


/**
 * Some entity UI components has gridSizes settings therefore we will make an interface for this and provide helper functions
 * to validate the settings so that we don't have to repeat this in many places.
 */
export interface GridSizeSettings {
    readonly xs?: GridSize | undefined;
    readonly sm?: GridSize | undefined;
    readonly md?: GridSize | undefined;
    readonly lg?: GridSize | undefined;
    readonly xl?: GridSize | undefined;
}


export interface GridCustomSettings {
    /**
     * A map of grid size settings for example
     *  {
     *      xs: 12,     // display the items 1 per row on small screen
     *      md: 6       // display the items 2 per row on medium screen
     *      ...
     *  }
     */
    readonly gridSizes?: GridSizeSettings | undefined;
}

/**
 * Type guard function to check if an object is a valid GridSizeSettings
 */
const isGridSizeSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is GridSizeSettings => {
    const settings = obj as GridSizeSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.xs, false, false, 1, 12) && !isValidSettingStringValue(settings.xs, false, false, ['auto'])) {
        return errorLogger?.logError({
            message: '"xs" must be undefined or an integer between 1 and 12 or value "auto".',
            value  : settings.xs,
            path   : [...configPath, 'xs'],
        }) ?? false;
    }
    if (!isValidSettingNumberValue(settings.sm, false, false, 1, 12) && !isValidSettingStringValue(settings.sm, false, false, ['auto'])) {
        return errorLogger?.logError({
            message: '"sm" must be undefined or an integer between 1 and 12 or value "auto".',
            value  : settings.sm,
            path   : [...configPath, 'sm'],
        }) ?? false;
    }
    if (!isValidSettingNumberValue(settings.md, false, false, 1, 12) && !isValidSettingStringValue(settings.md, false, false, ['auto'])) {
        return errorLogger?.logError({
            message: '"md" must be undefined or an integer between 1 and 12 or value "auto".',
            value  : settings.md,
            path   : [...configPath, 'md'],
        }) ?? false;
    }
    if (!isValidSettingNumberValue(settings.lg, false, false, 1, 12) && !isValidSettingStringValue(settings.lg, false, false, ['auto'])) {
        return errorLogger?.logError({
            message: '"lg" must be undefined or an integer between 1 and 12 or value "auto".',
            value  : settings.lg,
            path   : [...configPath, 'lg'],
        }) ?? false;
    }
    if (!isValidSettingNumberValue(settings.xl, false, false, 1, 12) && !isValidSettingStringValue(settings.xl, false, false, ['auto'])) {
        return errorLogger?.logError({
            message: '"xl" must be undefined or an integer between 1 and 12 or value "auto".',
            value  : settings.xl,
            path   : [...configPath, 'xl'],
        }) ?? false;
    }

    return true;
};


/**
 * Type guard function to check if an object is a valid GridCustomSettings
 */
export const isGridCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is GridCustomSettings => {
    const settings = obj as GridCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (settings.gridSizes !== undefined && !isGridSizeSettings(settings.gridSizes, errorLogger, [...configPath, 'gridSizes'])) {
        return false;
    }

    return true;
};


/**
 * The interface for storing settings for 1 action
 */
export interface EntityUIComponentCustomAction {
    readonly icon?: string | undefined;
    readonly label?: string | undefined;
    readonly variant?: ButtonProps['variant'] | undefined;
    readonly color?: ButtonProps['color'] | undefined;

    // The URL where the component should take the user to when the action is clicked. By default, the component will take the user
    // to where it needs to take the user to.
    readonly link?: string | undefined;

    readonly actions?: readonly EntityUIComponentCustomAction[] | undefined;
}



const isEntityUIComponentCustomAction = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityUIComponentCustomAction => {
    const settings = obj as EntityUIComponentCustomAction;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // icon, label, variant, and color are optional but if they are defined, they must be a non-empty string
    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.label)) {
        return errorLogger?.logError({
            message: '"label" must be undefined or a non-empty string',
            value  : settings.label,
            path   : [...configPath, 'label'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.variant, false, false, ['text', 'outlined', 'contained'])) {
        return errorLogger?.logError({
            message: '"variant" must be undefined or one of the following ["text", "outlined", "contained"]',
            value  : settings.variant,
            path   : [...configPath, 'variant'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.color, false, false, ['inherit', 'primary', 'secondary', 'default'])) {
        return errorLogger?.logError({
            message: '"color" must be undefined or one of the following ["inherit", "primary", "secondary", "default"]',
            value  : settings.color,
            path   : [...configPath, 'color'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.link)) {
        return errorLogger?.logError({
            message: '"link" must be undefined or a non-empty string',
            value  : settings.link,
            path   : [...configPath, 'link'],
        }) ?? false;
    }

    // actions is optional but if it is defined, it must be an Array of EntityUIComponentCustomAction
    if (settings.actions !== undefined) {
        if (!(settings.actions instanceof Array)) {
            return errorLogger?.logError({
                message: '"actions" must be undefined or an Array of actions',
                value  : settings.actions,
                path   : [...configPath, 'actions'],
            }) ?? false;
        }

        // if one of the actions is not a valid EntityUIComponentCustomAction then return false
        if (!settings.actions.every((action, index) => {
            return isEntityUIComponentCustomAction(action, errorLogger, [...configPath, 'actions', `[${index}]`]);
        })) {
            return false;
        }
    }

    return true;
};


/**
 * Some entity UI components has "actions" settings which allow the admin to configure the set of actions to display in the component.
 * Therefore we will make an interface for this and provide helper functions to validate the settings so that we don't have to repeat
 * this in many components.
 */
export interface EntityUIComponentCustomActionsSettings {
    readonly actions?: readonly EntityUIComponentCustomAction[] | undefined;
}



/**
 * Type guard function to check if an object is a valid EntityUIComponentCustomActionsSettings
 */
export const isEntityUIComponentCustomActionsSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityUIComponentCustomActionsSettings => {
    const settings = obj as EntityUIComponentCustomActionsSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // actions is optional but if it is defined, it must be an Array of EntityUIComponentCustomAction
    if (settings.actions !== undefined) {
        if (!(settings.actions instanceof Array)) {
            return errorLogger?.logError({
                message: '"actions" must be undefined or an Array of actions',
                value  : settings.actions,
                path   : [...configPath, 'actions'],
            }) ?? false;
        }

        // if one of the actions is not a valid EntityUIComponentCustomAction then return false
        if (!settings.actions.every((action, index) => {
            return isEntityUIComponentCustomAction(action, errorLogger, [...configPath, 'actions', `[${index}]`]);
        })) {
            return false;
        }
    }

    return true;
};

/** ************************    END STRUCTURES FOR CUSTOMIZING THE UI PER ENTITY CLASS   ***************************** */
